// src/Flights.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// NavBar is now rendered in App.js
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Typography,
  TextField, // <-- Import TextField for the confirmation input
  Stack, // <-- Import Stack for layout
} from '@mui/material';

function Flights() {
  const navigate = useNavigate();
  const [flightList, setFlightList] = useState([]);

  // State for individual delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flightToDelete, setFlightToDelete] = useState(null);

  // --- NEW State for "Delete All" dialog ---
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');
  // -----------------------------------------

  useEffect(() => {
    let storedFlights = [];
    try {
      storedFlights = JSON.parse(localStorage.getItem('flightList') || '[]');
      if (!Array.isArray(storedFlights)) storedFlights = [];
    } catch (e) {
      console.warn('Corrupt flightList in localStorage; resetting.', e);
      storedFlights = [];
    }
    setFlightList(storedFlights);
  }, []);

  // --- Individual Delete Handlers ---
  const handleViewFlight = (flight) => {
    navigate('/flight-data', {
      state: {
        tdrData: flight.tdrData,
        brockData: flight.brockData,
        freightManifestData: flight.freightManifestData, // Add freight manifest data
        parseWarnings: flight.parseWarnings || [],
        brockWarnings: flight.brockWarnings || [],
        tdrMissingFlights: flight.tdrMissingFlights || [],
        tdrMissingMass: flight.tdrMissingMass || false,
        breakdownParams: flight.breakdownParams || {
          isPriorityFlight: false,
          hasFreightManifest: false,
          hotThreshold: 30,
          coldThresholdHours: 2,
        },
      },
    });
    console.log('Viewing flight with parameters:', flight.breakdownParams);
  };

  const handleOpenDeleteDialog = (index) => {
    setFlightToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFlightToDelete(null);
  };

  const handleDeleteFlight = () => {
    if (flightToDelete !== null) {
      const updatedFlights = [...flightList];
      updatedFlights.splice(flightToDelete, 1);
      setFlightList(updatedFlights);
      localStorage.setItem('flightList', JSON.stringify(updatedFlights));
      handleCloseDeleteDialog();
    }
  };
  // ---------------------------------

  // --- NEW "Delete All" Handlers ---
  const handleOpenDeleteAllDialog = () => {
    setConfirmDeleteText(''); // Reset confirmation text
    setDeleteAllDialogOpen(true);
  };

  const handleCloseDeleteAllDialog = () => {
    setDeleteAllDialogOpen(false);
    setConfirmDeleteText(''); // Reset on close as well
  };

  const handleConfirmTextChange = (event) => {
    setConfirmDeleteText(event.target.value);
  };

  const handleConfirmDeleteAll = () => {
    // Double-check confirmation text before deleting
    if (confirmDeleteText.toLowerCase() === 'delete') {
      setFlightList([]); // Clear state
      localStorage.removeItem('flightList'); // Clear localStorage
      handleCloseDeleteAllDialog(); // Close the dialog
    }
  };
  // ---------------------------------

  // Determine if the "Confirm Delete All" button should be enabled
  const isDeleteAllConfirmEnabled = confirmDeleteText.toLowerCase() === 'delete';

  return (
    <div style={{ color: '#fff', minHeight: '100vh' }}>
      {/* NavBar and offset are now handled in App.js */}
      <div style={{ padding: '2rem' }}>
        {/* --- Heading and Delete All Button --- */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: '1rem' }}
        >
          <h2 style={{ color: '#F9B612', margin: 0 }}>Saved Flights</h2>
          {/* Show Delete All button only if there are flights */}
          {flightList.length > 0 && (
            <Button
              className="flight-delete-button flight-delete-button--all"
              variant="contained"
              color="error"
              onClick={handleOpenDeleteAllDialog}
              sx={{
                backgroundColor: '#dc3545', // More explicit error color
                '&:hover': {
                  backgroundColor: '#c82333',
                },
              }}
            >
              Delete All Flights
            </Button>
          )}
        </Stack>
        {/* ------------------------------------ */}

        {flightList.length === 0 ? (
          <p>No saved flights yet.</p>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#1f2937' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#fff' }}>Inbound Flight #</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Timestamp</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Parameters</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flightList.map((flight, idx) => {
                  const inboundNum = flight.tdrData?.inbound?.flightNumber || 'Unknown';
                  const inboundCity = flight.tdrData?.inbound?.arrivingFrom || '';
                  const timeString = new Date(flight.timestamp).toLocaleString();
                  const isPriorityFlight = flight.breakdownParams?.isPriorityFlight === true;
                  const hotThreshold = flight.breakdownParams?.hotThreshold || 30;
                  const coldThresholdHours = flight.breakdownParams?.coldThresholdHours || 2;

                  return (
                    <TableRow key={idx}>
                      <TableCell sx={{ color: '#fff' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1">{inboundNum}</Typography>
                          {inboundCity && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#F9B612',
                                fontSize: '0.8rem',
                                fontWeight: 'normal',
                              }}
                            >
                              {inboundCity}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>{timeString}</TableCell>
                      <TableCell>
                        {isPriorityFlight ? (
                          <Chip
                            className="flight-chip flight-chip--priority"
                            label="Priority Flight"
                            size="small" // Make chips smaller
                            sx={{
                              backgroundColor: 'rgba(249, 182, 18, 0.3)',
                              color: '#F9B612',
                              fontWeight: 'bold',
                              mr: 1,
                            }}
                          />
                        ) : (
                          <Chip
                            className="flight-chip flight-chip--hot"
                            label={`Hot ≤ ${hotThreshold}min`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(255, 0, 0, 0.2)',
                              color: '#fff',
                              mr: 1,
                            }}
                          />
                        )}
                        <Chip
                          className="flight-chip flight-chip--cold"
                          label={`Cold ≥ ${coldThresholdHours}hr`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 255, 0.2)',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}> {/* Reduced gap */}
                          <Button
                            variant="outlined"
                            size="small" // Smaller buttons
                            sx={{ color: '#fff', borderColor: '#fff' }}
                            onClick={() => handleViewFlight(flight)}
                          >
                            View
                          </Button>
                          <Button
                            className="flight-delete-button"
                            variant="outlined"
                            size="small"
                            sx={{
                              color: '#ff0000',
                              borderColor: '#ff0000',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#ff0000',
                                backgroundColor: 'rgba(255,0,0,0.1)',
                              },
                            }}
                            onClick={() => handleOpenDeleteDialog(idx)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      {/* --- Individual Delete Confirmation Dialog --- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{ style: { backgroundColor: '#1f2937', color: '#fff' } }}
      >
        <DialogTitle>{"Delete Flight"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#e2e2e2' }}>
            Are you sure you want to delete this flight? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ color: '#fff' }}>Cancel</Button>
          <Button
            className="flight-delete-button"
            onClick={handleDeleteFlight}
            sx={{ color: '#ff0000' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* ------------------------------------------- */}

      {/* --- NEW "Delete All" Confirmation Dialog --- */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleCloseDeleteAllDialog}
        PaperProps={{ style: { backgroundColor: '#1f2937', color: '#fff' } }}
      >
        <DialogTitle>{"Confirm Delete All Flights"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#e2e2e2', marginBottom: 2 }}>
            This action will permanently delete all saved flights. This cannot be undone.
            <br />
            To confirm, please type 'delete' in the box below.
          </DialogContentText>
          <TextField
            autoFocus // Focus the input field when dialog opens
            margin="dense"
            id="confirm-delete"
            label="Type 'delete' to confirm"
            type="text"
            fullWidth
            variant="outlined"
            value={confirmDeleteText}
            onChange={handleConfirmTextChange}
            InputLabelProps={{ style: { color: '#ccc' } }} // Style label
            InputProps={{ style: { color: '#fff' } }} // Style input text
            sx={{
              '& .MuiOutlinedInput-root': { // Style border
                '& fieldset': { borderColor: '#ccc' },
                '&:hover fieldset': { borderColor: '#fff' },
                '&.Mui-focused fieldset': { borderColor: '#F9B612' },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteAllDialog} sx={{ color: '#fff' }}>Cancel</Button>
          <Button
            className="flight-delete-button flight-delete-button--all"
            onClick={handleConfirmDeleteAll}
            color="error" // Use error color for the button
            variant="contained" // Make it stand out
            disabled={!isDeleteAllConfirmEnabled} // Disable based on text match
            sx={{
              backgroundColor: isDeleteAllConfirmEnabled ? '#dc3545' : '#555', // Red when enabled, grey when disabled
              '&:hover': {
                backgroundColor: isDeleteAllConfirmEnabled ? '#c82333' : '#555',
              },
              // Style disabled state explicitly if needed
              '&.Mui-disabled': {
                 backgroundColor: '#555',
                 color: '#999'
              }
            }}
          >
            Confirm Delete All
          </Button>
        </DialogActions>
      </Dialog>
      {/* ------------------------------------------ */}
    </div>
  );
}

export default Flights;
